/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mentorDevelopmentApi } from 'api/mentorDevelopment';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import {
	MentorDevelopmentAreaComputed,
	MentorDevelopmentAreaKey,
	MentorDevelopmentAreaView,
	MentorDevelopmentGetResponse,
	MentorDevelopmentStepTranslated,
	UpdateMentorDevelopmentScoreRequest,
} from './mentorDevelopment.types';

export const mentorDevelopmentQueryKey = (applicationId: number) => ['MENTOR_DEVELOPMENT', { applicationId }];
export const mentorDevelopmentProgressSummaryQueryKey = (applicationId: number) => [
	'MENTOR_DEVELOPMENT_PROGRESS_SUMMARY',
	{ applicationId },
];

const mentorAreaTranslationMap: Record<MentorDevelopmentAreaKey, string> = {
	[MentorDevelopmentAreaKey.GOALS_SETTING]: 'userPanel.mentorDevelopment.areas.goalsSetting',
	[MentorDevelopmentAreaKey.GIVING_FEEDBACK]: 'userPanel.mentorDevelopment.areas.givingFeedback',
	[MentorDevelopmentAreaKey.LISTENING_SKILLS]: 'userPanel.mentorDevelopment.areas.listeningSkills',
	[MentorDevelopmentAreaKey.QUESTIONING_SKILLS]: 'userPanel.mentorDevelopment.areas.questioningSkills',
	[MentorDevelopmentAreaKey.COMMUNICATION_SKILLS]: 'userPanel.mentorDevelopment.areas.communicationSkills',
	[MentorDevelopmentAreaKey.RELATION_MANAGEMENT]: 'userPanel.mentorDevelopment.areas.relationManagement',
	[MentorDevelopmentAreaKey.MENTOR_DEVELOPMENT]: 'userPanel.mentorDevelopment.areas.mentorDevelopment',
};

const getAreaTranslationKey = (areaKey: MentorDevelopmentAreaKey, field: 'title' | 'about' | 'value') =>
	`${String(mentorAreaTranslationMap[areaKey] || `[${areaKey}]`)}.${field}`;

type UpdateDataAreaPayload = {
	selectedForDevelopment: boolean;
	initialScore: number | null;
	finalScroe: number | null;
	steps: MentorDevelopmentStepTranslated[];
};

export const useMentorDevelopment = (applicationId: number, enabled = true) => {
	const queryClient = useQueryClient();
	const { tProgram } = useProgramTranslation();

	const queryKey = mentorDevelopmentQueryKey(applicationId);
	const progressSummaryQueryKey = mentorDevelopmentProgressSummaryQueryKey(applicationId);
	const clearProgressSummaryCache = useCallback(() => {
		queryClient.removeQueries({ queryKey: progressSummaryQueryKey, exact: true });
	}, [progressSummaryQueryKey, queryClient]);

	// pending area keys
	const [pendingAreaKeys, setPendingAreaKeys] = useState<string[]>([]);
	const addPendingAreaKey = useCallback((areaKey: MentorDevelopmentAreaKey) => {
		setPendingAreaKeys((prev) => Array.from(new Set([...prev, areaKey])));
	}, []);
	const removePendingAreaKey = useCallback((areaKey: MentorDevelopmentAreaKey) => {
		setPendingAreaKeys((prev) => prev.filter((key) => key !== areaKey));
	}, []);

	// pending step ids
	const [pendingStepIds, setPendingStepIds] = useState<number[]>([]);
	const addPendingStepId = useCallback((stepId: number) => {
		setPendingStepIds((prev) => Array.from(new Set([...prev, stepId])));
	}, []);
	const removePendingStepId = useCallback((stepId: number) => {
		setPendingStepIds((prev) => prev.filter((key) => key !== stepId));
	}, []);

	const [selectedAreaKeys, setSelectedAreaKeys] = useState<string[]>([]);

	const [dataAreaCache, setDataAreaCache] = useState<MentorDevelopmentGetResponse>([]);
	const { data, isLoading, isError, error } = useQuery(
		queryKey,
		() => mentorDevelopmentApi.getMentorDevelopment(applicationId),
		{
			enabled: enabled && Boolean(applicationId),
			staleTime: Infinity,
			refetchOnMount: 'always',
			refetchOnWindowFocus: true,
			select: (response) =>
				response.sort(({ sequence: sequenceA }, { sequence: sequenceB }) => (sequenceA >= sequenceB ? 1 : -1)),
			onSuccess: (response) => {
				setDataAreaCache(response);
				setSelectedAreaKeys(response.filter((area) => area.selectedForDevelopment).map((area) => area.areaKey));
			},
		}
	);

	useEffect(() => {
		if (!data) {
			return;
		}

		setDataAreaCache(data);
		setSelectedAreaKeys(data.filter((area) => area.selectedForDevelopment).map((area) => area.areaKey));
	}, [data]);

	const updateDataAreaCache = (
		areaKey: MentorDevelopmentAreaKey,
		data:
			| Partial<UpdateDataAreaPayload>
			| ((currentValue: Partial<UpdateDataAreaPayload>) => Partial<UpdateDataAreaPayload>),
		prevData?: MentorDevelopmentGetResponse
	) => {
		// queryClient.setQueryData<MentorDevelopmentGetResponse>(queryKey, (dataBeforeUpdate) => {
		// 	const oldData = dataBeforeUpdate || prevData;
		// 	// console.log('@update-area 1', { areaKey, dataBeforeUpdate, prevData });
		// 	if (oldData) {
		// 		const currentAreaView = (oldData || []).find(
		// 			(area) => area.areaKey === areaKey
		// 		) as MentorDevelopmentAreaView;
		// 		// console.log('@update-area 2', { areaKey, currentAreaView });
		// 		if (currentAreaView) {
		// 			const newAreaData = typeof data === 'function' ? data(currentAreaView) : data;
		// 			// console.log('@update-arae 3', { newAreaData });
		// 			const newData: MentorDevelopmentGetResponse = [
		// 				...(oldData || []).filter((area) => area.areaKey !== areaKey),
		// 				{
		// 					...currentAreaView,
		// 					...newAreaData,
		// 				},
		// 			].sort(({ sequence: sequenceA }, { sequence: sequenceB }) => (sequenceA >= sequenceB ? 1 : -1));
		// 			return newData;
		// 		}
		// 	}
		// 	return oldData;
		// });

		setDataAreaCache((oldData) => {
			if (oldData) {
				const currentAreaView = (oldData || []).find(
					(area) => area.areaKey === areaKey
				) as MentorDevelopmentAreaView;
				if (currentAreaView) {
					const newAreaData = typeof data === 'function' ? data(currentAreaView) : data;
					const newData: MentorDevelopmentGetResponse = [
						...(oldData || []).filter((area) => area.areaKey !== areaKey),
						{
							...currentAreaView,
							...newAreaData,
						},
					].sort(({ sequence: sequenceA }, { sequence: sequenceB }) => (sequenceA >= sequenceB ? 1 : -1));
					return newData;
				}
			}
			return oldData;
		});
	};

	const { mutateAsync: updateScoreMutateAsync } = useMutation(
		({ areaKey, score }: { areaKey: MentorDevelopmentAreaKey; score: UpdateMentorDevelopmentScoreRequest }) =>
			mentorDevelopmentApi.updateScore(applicationId, areaKey, score),
		{
			onSuccess: (response, variables) => {
				if (response) {
					updateDataAreaCache(
						variables.areaKey,
						{
							initialScore: response.initialScore,
							finalScroe: response.finalScore,
						},
						data
					);
				}
				clearProgressSummaryCache();
				removePendingAreaKey(variables.areaKey);
			},
			onError: (response, variables) => {
				removePendingAreaKey(variables.areaKey);
			},
		}
	);

	const updateScore = useCallback((areaKey: MentorDevelopmentAreaKey, score: UpdateMentorDevelopmentScoreRequest) => {
		updateDataAreaCache(areaKey, score as any);
		addPendingAreaKey(areaKey);
		return updateScoreMutateAsync({ areaKey, score });
	}, []);

	const { mutateAsync: toggleAreaMutateAsync } = useMutation(
		({ areaKey, selectedForDevelopment }: { areaKey: MentorDevelopmentAreaKey; selectedForDevelopment: boolean }) =>
			mentorDevelopmentApi.toggleMentorDevelopmentArea(applicationId, areaKey, selectedForDevelopment),
		{
			onSuccess: (response, variables) => {
				updateDataAreaCache(
					variables.areaKey,
					{
						selectedForDevelopment: response.selectedForDevelopment,
					},
					data
				);
				setSelectedAreaKeys((prev) =>
					response.selectedForDevelopment
						? Array.from(new Set([...prev, variables.areaKey]))
						: prev.filter((key) => key !== variables.areaKey)
				);
				clearProgressSummaryCache();
				removePendingAreaKey(variables.areaKey);
			},
			onError: (response, variables) => {
				removePendingAreaKey(variables.areaKey);
			},
		}
	);

	const toggleArea = useCallback((areaKey: MentorDevelopmentAreaKey, selectedForDevelopment: boolean) => {
		updateDataAreaCache(areaKey, (prev) => ({
			...prev,
			selectedForDevelopment,
		}));
		setSelectedAreaKeys((prev) =>
			selectedForDevelopment ? Array.from(new Set([...prev, areaKey])) : prev.filter((key) => key !== areaKey)
		);
		addPendingAreaKey(areaKey);
		return toggleAreaMutateAsync({ areaKey, selectedForDevelopment });
	}, []);

	const getStepAreaKeyByStepId = useCallback(
		(stepId: number, areas?: MentorDevelopmentAreaView[]) => {
			if (areas) {
				// eslint-disable-next-line no-restricted-syntax
				for (const { steps, areaKey } of areas) {
					if (steps.map(({ id }) => id).includes(stepId)) {
						return areaKey;
					}
				}
			}
			return null;
		},
		[data]
	);

	const { mutateAsync: toggleStepMutateAsync } = useMutation(
		({ stepId, achieved }: { stepId: number; achieved: boolean }) =>
			mentorDevelopmentApi.toggleMentorDevelopmentStep(applicationId, stepId, achieved),
		{
			onSuccess: (response, variables) => {
				const stepAreaKey = getStepAreaKeyByStepId(variables.stepId, data);
				if (stepAreaKey) {
					updateDataAreaCache(
						stepAreaKey,
						(prev) => ({
							...prev,
							steps: (prev?.steps || []).map((step) => {
								if (step.id === variables.stepId) {
									return {
										...step,
										achieved: response.achieved,
									};
								}
								return step;
							}),
						}),
						data
					);
					removePendingAreaKey(stepAreaKey);
				}
				clearProgressSummaryCache();
				removePendingStepId(variables.stepId);
			},
			onError: (response, variables) => {
				const stepAreaKey = getStepAreaKeyByStepId(variables.stepId, data);
				if (stepAreaKey) {
					removePendingAreaKey(stepAreaKey);
				}
				removePendingStepId(variables.stepId);
			},
		}
	);

	const toggleStep = useCallback(
		(stepId: number, achieved: boolean) => {
			const stepAreaKey = getStepAreaKeyByStepId(stepId, data);
			if (stepAreaKey) {
				updateDataAreaCache(stepAreaKey, (prev) => ({
					...prev,
					steps: (prev?.steps || []).map((step) => {
						if (step.id === stepId) {
							return {
								...step,
								achieved,
							};
						}
						return step;
					}),
				}));
				addPendingAreaKey(stepAreaKey);
				addPendingStepId(stepId);
				return toggleStepMutateAsync({ stepId, achieved });
			}

			return null;
		},
		[data]
	);

	const areas: MentorDevelopmentAreaComputed[] = useMemo(
		() =>
			dataAreaCache.map(({ areaKey, initialScore, finalScore, selectedForDevelopment, sequence, steps }) => ({
				areaKey,
				title: tProgram(getAreaTranslationKey(areaKey, 'title')),
				info: {
					about: tProgram(getAreaTranslationKey(areaKey, 'about')),
					value: tProgram(getAreaTranslationKey(areaKey, 'value')),
				},
				initialScore,
				finalScore,
				selectedForDevelopment,
				sequence,
				steps,
			})),
		[dataAreaCache]
	);

	const selectedAreas = (areas || []).filter(({ areaKey }) => selectedAreaKeys.includes(areaKey));

	const actions = {
		toggleArea,
		updateScore,
		toggleStep,
	};

	const getAreaMetaText = (areaKey: MentorDevelopmentAreaKey, field: 'about' | 'value') =>
		tProgram(getAreaTranslationKey(areaKey, field));

	return {
		isLoading,
		isError,
		error,
		areas,
		selectedAreas,
		pendingAreaKeys,
		pendingStepIds,
		actions,
		getAreaMetaText,
	};
};
